import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { TrialItemTemplatesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsTrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialActions from "../../../actions/trial";
import * as TrialItemTemplateActions from "../../../actions/trialItemTemplate";
import { FormFilterInput, SelectFormInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialItemTemplatesPage.scss";

interface TrialItemTemplatesPageProps {
    trials: Dtos.Trial[];
    loadingTrials: boolean;
    loadTrialsSuccess: boolean;
    loadTrialsFailure: boolean;
    loadTrialsErrorCode: string;
    trialItemTemplates: Dtos.TrialItemTemplate[];
    trialItemsByCategory: Dtos.Category[];
    formFilter: IFormFilter;
    lookups: Dtos.Lookup[];
    loadingTrialItemTemplates: boolean;
    loadTrialItemTemplatesSuccess: boolean;
    loadTrialItemTemplatesFailure: boolean;
    loadTrialItemTemplatesErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearTrials: TrialActions.ITrialClearActionCreator;
    loadTrials: TrialActions.ITrialLoadActionCreator;
    clearTrialItemTemplates: TrialItemTemplateActions.ITrialItemTemplateClearActionCreator;
    loadTrialItemTemplates: TrialItemTemplateActions.ITrialItemTemplateLoadActionCreator;
    filter: TrialItemTemplateActions.ITrialItemTemplateFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialItemTemplatesPage: FC<TrialItemTemplatesPageProps> = ({
    trials,
    loadingTrials,
    loadTrialsFailure,
    loadTrialsErrorCode,
    trialItemTemplates,
    trialItemsByCategory,
    formFilter,
    lookups,
    loadingTrialItemTemplates,
    loadTrialItemTemplatesSuccess,
    loadTrialItemTemplatesFailure,
    loadTrialItemTemplatesErrorCode,
    permissions,
    clearTrials,
    loadTrials,
    clearTrialItemTemplates,
    loadTrialItemTemplates,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{
        trialId: string;
    }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [trialIdState, setTrialIdState] = useState<number | null>(
        trialIdNumber
    );
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const loading = loadingTrialItemTemplates || loadingTrials;
    const showTrialItems =
        !loadingTrialItemTemplates &&
        loadTrialItemTemplatesSuccess &&
        !creatingTree;


    // Load Trials, Institutions & Items
    useEffect(() => {
        setDrawerItems(SettingsTrialSubMenu(permissions));
        setBreadcrumbs(
            TrialItemTemplatesBreadcrumbs(
                isNaN(trialIdNumber) ? undefined : trialIdNumber
            )
        );

        // Dropdown options
        if (!trials || trials.length === 0) {
            loadTrials();
        }

        // Load templates
        if (
            trialIdState &&
            !loadingTrialItemTemplates &&
            !loadTrialItemTemplatesSuccess
        ) {
            loadTrialItemTemplates(trialIdState);
        }
    }, [
        loadTrials,
        setDrawerItems,
        SettingsTrialSubMenu,
        setBreadcrumbs,
        TrialItemTemplatesBreadcrumbs,
        trials,
        trialIdState,
        trialItemTemplates,
        permissions,
        loadTrialItemTemplatesSuccess,
        loadTrialItemTemplates,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialsFailure) {
            navigate(`/error/${loadTrialsErrorCode}`);
        }
        if (loadTrialItemTemplatesFailure) {
            navigate(`/error/${loadTrialItemTemplatesErrorCode}`);
        }
    }, [
        loadTrialsFailure,
        navigate,
        loadTrialItemTemplatesFailure,
        loadTrialsErrorCode,
        loadTrialItemTemplatesErrorCode,
    ]);

    // Cleanup effect on component unmount
    useEffect(() => {
        return () => {
            clearTrials();
            clearTrialItemTemplates();
        };
    }, [clearTrials, clearTrialItemTemplates]);

    // Dropdown options memos
    const trialSelectOptions = useMemo(() => {
        if (!trials) return [];

        return trials
            ?.filter((x) => x.displayName)
            ?.sort((a, b) => a?.displayName.localeCompare(b?.displayName))
            ?.map((x) => ({ value: x.id, label: x?.displayName }));
    }, [trials]);

    // Reset tree initalisation on form filter change
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [trialItemsByCategory, formFilter, setInitalizedTreeData, trialId]);

    // Create tree
    useEffect(() => {
        if (trialItemsByCategory && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial items return
            if (!trialItemsByCategory || trialItemsByCategory?.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (trialItems) => {
                let children = [];

                children =
                    trialItems
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 4fr 4fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.FILE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.initialVersion}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 4fr 4fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Initial Version</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                trialItemsByCategory
                    ?.sort((a, b) => a?.rank - b?.rank)
                    ?.map((x) => ({
                        id: `category-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {x?.trialItemTemplates?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createChildNodes(x.trialItemTemplates),
                            ],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialItemsByCategory,
        initalizedTreeData,
        treeData,
        formFilter,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("item") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialItemView
                    )
                ) {
                    navigate(
                        trialIdState
                            ? `/settings/trial/itemtemplates/${trialIdState}/template/${id.replace(
                                  "item-",
                                  ""
                              )}`
                            : `/settings/trial/itemtemplates/template/${id.replace(
                                  "item-",
                                  ""
                              )}`
                    );
                }
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions]
    );

    const pageContent = useMemo(() => {
        return (
            <>
                <div className="row">
                    <div className={"col-md-12 col-lg-auto mb-2"}>
                        <h1>Trial Item Templates</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-md-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        <div
                            className="d-inline-table pr-2"
                            style={{ width: "300px" }}
                        >
                            <SelectFormInput
                                loading={loadingTrials}
                                multi={false}
                                options={trialSelectOptions}
                                clearable={true}
                                backspaceRemoves={false}
                                name={"trialIdState"}
                                id={"trialIdState"}
                                placeholder="Trial"
                                onChange={function (
                                    value: any,
                                    _options?: any
                                ): any {
                                    clearTrialItemTemplates();
                                    setTrialIdState(
                                        value != undefined
                                            ? parseInt(value)
                                            : null
                                    );
                                    return undefined;
                                }}
                                value={trialIdState}
                            />
                        </div>
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialItemCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    trialIdState
                                        ? `/settings/trial/itemtemplates/${trialIdState}/template/create`
                                        : "/settings/trial/itemtemplates/template/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {loading ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {!trialIdState ? (
                    <div className="card card-shadow p-3">
                        <div className="patient-tables">
                            <div className="alert alert-info d-flex mb-0">
                                <div className="mr-2">
                                    <FontAwesomeIcon
                                        icon={
                                            FontAwesomeIcons.Solid.INFO_SQUARE
                                        }
                                        fixedWidth
                                    />
                                </div>
                                <span>
                                    Select a trial to view item templates.
                                </span>
                            </div>
                        </div>
                    </div>
                ) : null}
                {showTrialItems && trialIdState && !loading ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </>
        );
    }, [
        generateOptionsFromLookup,
        findLookup,
        lookups,
        showTrialItems,
        trialIdState,
        setTrialIdState,
        trialItemTemplates,
        trialSelectOptions,
        loadingTrials,
        clearTrialItemTemplates,
        treeData,
        handleToggle,
        toggleState,
        loading,
    ]);

    return (
        <TrialLookupsLayout
            permissions={permissions}
            permission={Dtos.Permission.TrialItemView}
        >
            {pageContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialItemTemplatesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trials: state.trials.formFilteredData,
        loadingTrials:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Pending,
        loadTrialsSuccess:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Success,
        loadTrialsFailure:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Failure,
        loadTrialsErrorCode: state.trials.loadState.errorCode,
        trialItemTemplates: state.trialItemTemplate.formFilteredData!,
        trialItemsByCategory: state.trialItemTemplate.formFilteredDataGrouped,
        formFilter: state.trialItemTemplate.formFilter,
        lookups: state.trialItemTemplate.lookups,
        loadingTrialItemTemplates:
            state.trialItemTemplate.loadState &&
            state.trialItemTemplate.loadState.status == RequestState.Pending,
        loadTrialItemTemplatesSuccess:
            state.trialItemTemplate.loadState &&
            state.trialItemTemplate.loadState.status == RequestState.Success,
        loadTrialItemTemplatesFailure:
            state.trialItemTemplate.loadState &&
            state.trialItemTemplate.loadState.status == RequestState.Failure,
        loadTrialItemTemplatesErrorCode:
            state.trialItemTemplate.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialItemTemplatesPageProps> => {
    return {
        loadTrials: bindActionCreators(TrialActions.LoadTrial, dispatch),
        clearTrials: bindActionCreators(TrialActions.Clear, dispatch),
        loadTrialItemTemplates: bindActionCreators(
            TrialItemTemplateActions.LoadTrialItemTemplate,
            dispatch
        ),
        clearTrialItemTemplates: bindActionCreators(
            TrialItemTemplateActions.Clear,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        filter: bindActionCreators(TrialItemTemplateActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialItemTemplatesPage);
